// frontend/src/app/components/projects/project-corpus/paper-list.component.ts
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Component, EventEmitter, Input, Output, computed, inject, signal } from '@angular/core';
import {
  ImportSourceType,
  Page,
  PageRequest,
  PaperListFilter,
  PaperSortField,
  ProjectPaper,
  ProjectState,
  SortDirection,
} from '../../../project-corpus/domain/types';
import {
  WORK_TYPE_COMMON,
  SOURCE_TYPE_COMMON,
} from '../../../project-corpus/domain/openalex-types';
import { PaperRowComponent } from './paper-row.component';
import { ProjectContextService } from '../../../services/project-context.service';

interface SortOption {
  readonly value: string;
  readonly label: string;
  readonly field: PaperSortField;
  readonly direction: SortDirection;
}

const SORT_OPTIONS: readonly SortOption[] = [
  { value: 'addedAt:desc', label: 'Newest first', field: 'addedAt', direction: 'desc' },
  { value: 'addedAt:asc', label: 'Oldest first', field: 'addedAt', direction: 'asc' },
  { value: 'title:asc', label: 'Title (A→Z)', field: 'title', direction: 'asc' },
  { value: 'title:desc', label: 'Title (Z→A)', field: 'title', direction: 'desc' },
  { value: 'year:desc', label: 'Year (newest)', field: 'year', direction: 'desc' },
  { value: 'year:asc', label: 'Year (oldest)', field: 'year', direction: 'asc' },
  {
    value: 'firstAuthor:asc',
    label: 'First author (A→Z)',
    field: 'firstAuthor',
    direction: 'asc',
  },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

const SOURCE_OPTIONS: readonly { value: ImportSourceType | ''; label: string }[] = [
  { value: '', label: 'All sources' },
  { value: 'OPENALEX', label: 'OpenAlex' },
  { value: 'BIBTEX', label: 'BibTeX' },
  { value: 'PDF', label: 'PDF' },
  { value: 'GROUP_CORPUS', label: 'Group corpus' },
];

const WORK_TYPE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: '', label: 'All work types' },
  ...WORK_TYPE_COMMON,
];

const SOURCE_TYPE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: '', label: 'All venue types' },
  ...SOURCE_TYPE_COMMON,
];

/**
 * Paginated paper list with a responsive filter bar.
 *
 * Responsive:
 *  - Filter bar: `flex-col md:flex-row` — stacks on mobile, single row on
 *    desktop. The search input takes available width; the dropdowns and the
 *    toggle wrap.
 *  - Empty state CTA is centered both axes regardless of viewport.
 *  - Per-row layout is handled inside `PaperRowComponent` (cards on mobile,
 *    rows on desktop).
 *
 * State source of truth lives in `ProjectCorpusService`; this component
 * receives `papers`, `filter`, `pageReq`, and `projectState` as inputs and
 * emits change events back up.
 */
@Component({
  selector: 'app-paper-list',
  standalone: true,
  imports: [CommonModule, FormsModule, PaperRowComponent],
  templateUrl: './paper-list.component.html',
  styleUrls: ['./paper-list.component.scss'],
})
export class PaperListComponent {
  private ctx = inject(ProjectContextService);
  readonly sortOptions = SORT_OPTIONS;
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;
  readonly sourceOptions = SOURCE_OPTIONS;
  readonly workTypeOptions = WORK_TYPE_OPTIONS;
  readonly sourceTypeOptions = SOURCE_TYPE_OPTIONS;

  private readonly _papers = signal<Page<ProjectPaper> | null>(null);
  private readonly _filter = signal<PaperListFilter>({});
  private readonly _pageReq = signal<PageRequest>({
    page: 0,
    size: 25,
    sort: { field: 'addedAt', direction: 'desc' },
  });
  private readonly _projectState = signal<ProjectState>('INGESTION');
  private readonly _runningImportJobIds = signal<readonly string[]>([]);

  @Input()
  set papers(value: Page<ProjectPaper> | null) {
    this._papers.set(value);
  }
  get papers(): Page<ProjectPaper> | null {
    return this._papers();
  }

  @Input()
  set filter(value: PaperListFilter) {
    this._filter.set(value ?? {});
  }
  get filter(): PaperListFilter {
    return this._filter();
  }

  @Input()
  set pageReq(value: PageRequest) {
    this._pageReq.set(value);
  }
  get pageReq(): PageRequest {
    return this._pageReq();
  }

  @Input()
  set projectState(value: ProjectState) {
    this._projectState.set(value);
  }
  get projectState(): ProjectState {
    return this._projectState();
  }

  @Input()
  set runningImportJobIds(value: readonly string[] | null) {
    this._runningImportJobIds.set(value ?? []);
  }
  get runningImportJobIds(): readonly string[] {
    return this._runningImportJobIds();
  }

  private readonly _canEdit = signal(true);
  /** When false (e.g. the viewer is not a project member), rows are read-only. */
  @Input()
  set canEdit(value: boolean) {
    this._canEdit.set(value);
  }
  get canEdit(): boolean {
    return this._canEdit();
  }

  private readonly _loading = signal(false);
  /** When true, show skeleton rows instead of the current list. */
  @Input()
  set loading(value: boolean) {
    this._loading.set(value);
  }
  get loading(): boolean {
    return this._loading();
  }

  @Output() filterChange = new EventEmitter<PaperListFilter>();
  @Output() pageChange = new EventEmitter<PageRequest>();
  @Output() editPaper = new EventEmitter<ProjectPaper>();
  @Output() attachPdf = new EventEmitter<ProjectPaper>();
  @Output() viewDetailsPaper = new EventEmitter<ProjectPaper>();

  readonly currentPapers = this._papers.asReadonly();
  readonly currentFilter = this._filter.asReadonly();
  readonly currentPageReq = this._pageReq.asReadonly();
  readonly currentProjectState = this._projectState.asReadonly();
  readonly runningImportJobIdSet = computed(() => new Set(this._runningImportJobIds()));

  readonly isIngestionOpen = computed(() => {
    const s = this._projectState();
    return s === 'SETUP' || s === 'INGESTION';
  });

  /** Rows are read-only when ingestion is closed OR the viewer cannot edit (non-member). */
  readonly rowsReadonly = computed(() => !this.isIngestionOpen() || !this._canEdit());

  readonly needsAttentionActive = computed(() => this._filter().status === 'NEEDS_ATTENTION');

  readonly currentWorkTypeValue = computed(() => this._filter().workType ?? '');
  readonly currentSourceTypeValue = computed(() => this._filter().sourceType ?? '');

  readonly currentSortValue = computed(() => {
    const sort = this._pageReq().sort;
    if (!sort) return SORT_OPTIONS[0].value;
    return `${sort.field}:${sort.direction}`;
  });

  readonly currentSourceValue = computed(() => this._filter().source ?? '');

  readonly hasAnyFilter = computed(() => {
    const f = this._filter();
    return Boolean(f.q || f.source || f.status || f.importJobId || f.workType || f.sourceType);
  });

  readonly emptyState = computed<'loading' | 'no-papers' | 'no-matches' | 'closed' | null>(() => {
    if (this._loading()) return 'loading';
    const page = this._papers();
    if (page === null) return 'loading';
    if (page.items.length > 0) return null;
    if (!this.isIngestionOpen() && !this.hasAnyFilter()) return 'closed';
    if (this.hasAnyFilter()) return 'no-matches';
    return 'no-papers';
  });

  readonly totalElements = computed(() => this._papers()?.totalItems ?? 0);
  readonly totalPages = computed(() => this._papers()?.totalPages ?? 0);
  readonly currentPageIndex = computed(() => this._papers()?.currentPage ?? this._pageReq().page);
  readonly canPrev = computed(() => this.currentPageIndex() > 0 && !this._loading());
  readonly canNext = computed(
    () => this.currentPageIndex() < this.totalPages() - 1 && !this._loading(),
  );

  // ─── Filter handlers ────────────────────────────────────────────────────

  onSearchChange(q: string): void {
    const next: PaperListFilter = { ...this._filter() };
    const trimmed = q.trim();
    if (trimmed) {
      (next as { q?: string }).q = trimmed;
    } else {
      delete (next as { q?: string }).q;
    }
    this.filterChange.emit(next);
  }

  onSourceChange(source: string): void {
    const next: PaperListFilter = { ...this._filter() };
    if (source) {
      (next as { source?: ImportSourceType }).source = source as ImportSourceType;
    } else {
      delete (next as { source?: ImportSourceType }).source;
    }
    this.filterChange.emit(next);
  }

  onWorkTypeChange(value: string): void {
    const next: PaperListFilter = { ...this._filter() };
    if (value) {
      (next as { workType?: string }).workType = value;
    } else {
      delete (next as { workType?: string }).workType;
    }
    this.filterChange.emit(next);
  }

  onSourceTypeChange(value: string): void {
    const next: PaperListFilter = { ...this._filter() };
    if (value) {
      (next as { sourceType?: string }).sourceType = value;
    } else {
      delete (next as { sourceType?: string }).sourceType;
    }
    this.filterChange.emit(next);
  }

  toggleNeedsAttention(): void {
    const current = this._filter();
    const next: PaperListFilter = { ...current };
    if (current.status === 'NEEDS_ATTENTION') {
      delete (next as { status?: PaperListFilter['status'] }).status;
    } else {
      (next as { status?: PaperListFilter['status'] }).status = 'NEEDS_ATTENTION';
    }
    this.filterChange.emit(next);
  }

  resetFilter(): void {
    this.filterChange.emit({});
  }

  onViewAuditTrail(paperToProjectId: number): void {
    this.ctx.openPaperAuditTrail(paperToProjectId);
  }

  // ─── Page / sort handlers ───────────────────────────────────────────────

  onSortChange(value: string): void {
    const opt = SORT_OPTIONS.find((o) => o.value === value) ?? SORT_OPTIONS[0];
    this.pageChange.emit({
      ...this._pageReq(),
      page: 0,
      sort: { field: opt.field, direction: opt.direction },
    });
  }

  onPageSizeChange(size: string | number): void {
    const sizeNum = typeof size === 'string' ? Number(size) : size;
    this.pageChange.emit({ ...this._pageReq(), page: 0, size: sizeNum });
  }

  goPrev(): void {
    if (!this.canPrev()) return;
    this.pageChange.emit({ ...this._pageReq(), page: this.currentPageIndex() - 1 });
  }

  goNext(): void {
    if (!this.canNext()) return;
    this.pageChange.emit({ ...this._pageReq(), page: this.currentPageIndex() + 1 });
  }

  // ─── Row action passthroughs ────────────────────────────────────────────

  onEdit(paper: ProjectPaper): void {
    this.editPaper.emit(paper);
  }

  onAttach(paper: ProjectPaper): void {
    this.attachPdf.emit(paper);
  }

  onViewDetails(paper: ProjectPaper): void {
    this.viewDetailsPaper.emit(paper);
  }
}
